import { Form as BaseForm } from 'components/Wizards/Form/Form'
import Styles from 'components/Wizards/Wizard/Form/Sections.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Paragraph } from 'blaze-ui/Paragraph/Paragraph'
import { Button } from 'blaze-ui/Button'
import { Edit3 } from 'blaze-ui/icons'
import { FormikErrors, FormikValues, useFormikContext } from 'formik'
import { Field } from 'components/Wizards/Wizard/Form/Field'
import { NextButton } from 'components/Wizards/Wizard/Form/NextButton'
import { FormSchema, FormStatus, ManualFieldsSectionProps } from 'components/Wizards/Wizard/Form/Types'
import { useSections } from 'components/Wizards/Wizard/Form/Sections/SectionsProvider'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

const ManualFieldsSection = ({ isComplete, onAutofill, onNext, onEdit, fields, header }: ManualFieldsSectionProps) => {
  const { values } = useFormikContext()
  const isDesktopWizardsUpdatesOn = useFlipperFlag('desktop_wizards_updates')
  const manualFieldValues = Object.entries(values as FormikValues)
    .filter(([fieldName]) => {
      const field = fields.find(([name]) => name === fieldName)
      return field && !field[1].spec.meta.hideManualSectionPreview
    })
    .map(([_, value]) => value)

  if (isComplete) {
    return (
      <BaseForm.Section onClick={onEdit}>
        <div className={Styles.completedContent}>
          <Heading level={5}>
            {isDesktopWizardsUpdatesOn ? 'Blaze filled out the rest based on this prompt' : 'Description'}
          </Heading>
          {manualFieldValues.map((manualFieldValues, index) => (
            <Paragraph key={`manual-field-value-${index}`} variant="secondary">
              {manualFieldValues}
            </Paragraph>
          ))}
          <div className={Styles.editButtonContainer}>
            <Button onPress={onEdit} variant="ghost" frontIcon={Edit3} size="sm">
              Edit
            </Button>
          </div>
        </div>
      </BaseForm.Section>
    )
  }

  return (
    <BaseForm.Section>
      <BaseForm.SectionContent>
        <BaseForm.Fields>
          {header}
          {fields.map(([name, schema], i) => (
            <Field key={name} name={name} schema={schema} autoFocus={i === 0} />
          ))}
          <div className={Styles.nextButtonContainer}>
            <NextButton fields={fields} onNext={onNext} variant="secondary" isDisabled={!manualFieldValues.length}>
              I'll fill it in myself
            </NextButton>
            <NextButton fields={fields} onNext={onAutofill} isDisabled={!manualFieldValues.length}>
              Have Blaze autofill the rest
            </NextButton>
          </div>
        </BaseForm.Fields>
      </BaseForm.SectionContent>
    </BaseForm.Section>
  )
}

export const ManualFieldsSectionContainer = (props: Pick<ManualFieldsSectionProps, 'header'>) => {
  const { setStatus, status, initialStatus } = useFormikContext<{ [key: string]: any }>()
  const { manualFields, createFieldsetCompletion } = useSections()

  const visibleManualFields = manualFields.filter(([, schema]: [string, FormSchema]) => {
    return !schema.describe().meta.hide
  })

  if (visibleManualFields.length < 1) {
    return null
  }

  const handleAutofill = (errors: FormikErrors<any>) => {
    if (Object.keys(errors).length < 1) {
      setStatus(FormStatus.ManuallyFilled)
      return createFieldsetCompletion()
    }
  }

  const handleNext = (errors: FormikErrors<any>) => {
    if (Object.keys(errors).length < 1) {
      setStatus(FormStatus.ManuallyFilled)
    }
  }

  return (
    <ManualFieldsSection
      {...props}
      fields={visibleManualFields}
      onAutofill={handleAutofill}
      onNext={handleNext}
      onEdit={() => setStatus(initialStatus)}
      isComplete={[FormStatus.ManuallyFilled, FormStatus.AutoFilling, FormStatus.AutoFilled].includes(status)}
    />
  )
}
